import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { IonicModule, IonInput } from '@ionic/angular';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoElementPredicate } from '@maskito/core';
import {
  maskitoNumberOptionsGenerator,
  maskitoParseNumber,
  maskitoStringifyNumber,
} from '@maskito/kit';

export interface NumberInputConfig {
  required?: boolean;
  disabled?: boolean;
  // Unlike disabled, a readonly control keeps its value in the form's value.
  readonly?: boolean;
  placeholder?: string;
}

export interface NumberInputFormat {
  decimalSeparator: string;
  thousandSeparator: string;
  minimumFractionDigits: number;
  // 0 takes no decimal separator at all.
  maximumFractionDigits: number;
}

// The money field's.
const NUMBER_INPUT_FORMAT_DEFAULT: NumberInputFormat = {
  decimalSeparator: '.',
  thousandSeparator: ',',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

@Component({
  selector: 'ts-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, ReactiveFormsModule, MaskitoDirective],
})
export class NumberInputComponent implements OnChanges {
  @Input() config?: NumberInputConfig;
  @Input() format: NumberInputFormat = NUMBER_INPUT_FORMAT_DEFAULT;
  @Input() value?: number | null;
  @Output() valueChange = new EventEmitter<number | null>();

  @ViewChild(IonInput) ionInput!: IonInput;

  unmaskedValue: number | null = null;
  formControl = new FormControl<string>('');

  options = maskitoNumberOptionsGenerator(this.format);

  private unmask(value: string | null) {
    const unmasked = maskitoParseNumber(value ?? '', this.format);
    if (isNaN(unmasked)) {
      return null;
    }
    return unmasked;
  }

  elementPredicate: MaskitoElementPredicate = async (element) =>
    (element as unknown as HTMLIonInputElement).getInputElement();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      const config: NumberInputConfig = changes['config'].currentValue;
      const disabled = config?.disabled ?? false;
      if (disabled) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    }

    if (changes['format']) {
      this.options = maskitoNumberOptionsGenerator(this.format);
    }

    if (changes['value']) {
      const value: number | null = changes['value'].currentValue ?? null;
      // The echo of a typed number leaves the text alone: rewriting it would
      // drop a separator being typed ("1," shown as "1").
      if (value !== this.unmask(this.formControl.value)) {
        const maskedValue = maskitoStringifyNumber(value, this.format);
        this.formControl.setValue(maskedValue);
        this.unmaskedValue = this.unmask(maskedValue);
      }
    }
  }

  inputChanged() {
    this.unmaskedValue = this.unmask(this.formControl.value);
    this.valueChange.emit(this.unmaskedValue);
  }
}
